import {getCurrentUserIdentifiers} from './auth';
import { startLoading, stopLoading } from '../config/nprogress';

/**
 * API Utility Functions
 *
 * Architecture:
 * 1. settings.json (loaded at runtime) - Contains environment-specific URLs
 * 2. api-endpoints.ts - Defines API paths and helper functions
 * 3. This file - Handles authentication and makes actual API calls
 *
 * This separation allows us to:
 * - Change endpoints per environment without code changes
 * - Keep API path structure consistent
 * - Centralize authentication logic
 */

// List of endpoints to exclude from loading bar (e.g., WebSocket, polling, chat)
const EXCLUDED_ENDPOINTS = [
  '/chat',
  '/websocket',
];

/**
 * Get the API input context for the current session
 * This provides the authentication headers and body required for API calls
 */
export async function getAPIInputContext() {
  try {
    const [idToken, accessToken, username] = await getCurrentUserIdentifiers();

    return {
      headers: {
        "Authorization": idToken,
        "Content-Type": "application/json",
        Accept: 'application/json',
      } as Record<string, string>,
      body: {
        'userId': username,
        'accessToken': accessToken,
      }
    };
  } catch (error) {
    console.error('Error getting API input context:', error);
    // Return minimal context for unauthenticated requests
    return {
      headers: {
        "Content-Type": "application/json",
        Accept: 'application/json',
      } as Record<string, string>,
      body: {}
    };
  }
}

/**
 * Get the API endpoint from settings
 * Settings are loaded dynamically based on the environment
 */
function getApiEndpoint(): string {
  const settings = (window as any).__ADDAPT_SETTINGS__;
  if (!settings?.apiConfig?.endpoints?.[0]?.endpoint) {
    throw new Error('API endpoint not configured. Please ensure settings.json is loaded.');
  }
  return settings.apiConfig.endpoints[0].endpoint;
}

/**
 * Make an authenticated API call
 * @param endpoint - The API endpoint path
 * @param method - HTTP method (GET, POST, PUT, DELETE)
 * @param data - Request body data
 * @param options - Additional options (e.g., skipLoading)
 * @returns Promise with the API response
 */
export async function makeApiCall(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'POST',
    data?: any,
    options?: { skipLoading?: boolean; maxRetries?: number }
) {
  // Check if we should show loading bar
  const shouldShowLoading = !options?.skipLoading && 
    !EXCLUDED_ENDPOINTS.some(excluded => endpoint.includes(excluded));
  
  if (shouldShowLoading) {
    startLoading();
  }

  const maxRetries = options?.maxRetries ?? 2;
  let lastError: Error | null = null;
  
  try {
    const context = await getAPIInputContext();
    const baseUrl = getApiEndpoint();

    const url = `${baseUrl}${endpoint}`;

    const requestOptions: RequestInit = {
      method,
      headers: context.headers,
      mode: 'cors',
    };

    if (method !== 'GET' && data) {
      requestOptions.body = JSON.stringify({
        ...context.body,
        ...data
      });
    }

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, requestOptions);

        // Retry on 429 (throttle) or 5xx (server error)
        if ((response.status === 429 || response.status >= 500) && attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
          console.warn(`API call ${endpoint} returned ${response.status}, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        // Retry once on 401 after refreshing the auth token
        if (response.status === 401 && attempt < maxRetries) {
          console.warn(`API call ${endpoint} returned 401, refreshing token and retrying`);
          try {
            const freshContext = await getAPIInputContext();
            if (requestOptions.headers) {
              (requestOptions.headers as Record<string, string>)['Authorization'] =
                freshContext.headers['Authorization'];
            }
            if (requestOptions.body && method !== 'GET') {
              const bodyObj = JSON.parse(requestOptions.body as string);
              bodyObj.accessToken = freshContext.body.accessToken;
              requestOptions.body = JSON.stringify(bodyObj);
            }
          } catch (refreshErr) {
            console.error('Token refresh failed:', refreshErr);
          }
          continue;
        }

        if (!response.ok) {
          let errorMessage = `API call failed: ${response.status} ${response.statusText}`;
          try {
            const errorData = await response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            }
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }
          throw new Error(errorMessage);
        }

        const responseData = await response.json();
        return responseData;
      } catch (error) {
        lastError = error as Error;
        // Retry on network errors (TypeError from fetch = network failure)
        const isNetworkError = error instanceof TypeError;
        if (isNetworkError && attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
          console.warn(`API call ${endpoint} network error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
    }

    throw lastError || new Error('API call failed after retries');
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  } finally {
    if (shouldShowLoading) {
      stopLoading();
    }
  }
}
